import { useCallback, useRef } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { API_VERSION, BASE_PATH } from '@common/api';
import { SignInActions, browserSessionId } from '@base/authentication/SignInActions';

export enum StreamEventType {
    STEP = 'step',
    TOKEN = 'token',
    COMPLETE = 'complete',
    ERROR = 'error',
}

interface StreamEvent {
    type: StreamEventType;
    data?: string;
    text?: string;
}

export interface StreamingChatCallbacks {
    onStep: (text: string) => void;
    onToken: (token: string) => void;
    onComplete: (response: string) => void;
    onError: (error: string) => void;
}

export function useChatStreamApi(callbacks: StreamingChatCallbacks) {
    const abortControllerRef = useRef<AbortController | null>(null);
    // Keep ref to latest callbacks so SSE event handlers never use stale closures
    const callbacksRef = useRef(callbacks);
    callbacksRef.current = callbacks;

    const sendStreamingMessage = useCallback(
        async (chatId: number, message: string, requestId: number, tenantId: string) => {
            abortControllerRef.current = new AbortController();

            const token = SignInActions.getToken();

            await fetchEventSource(`${BASE_PATH}/chat/askAI/stream`, {
                method: 'POST',
                headers: {
                    Accept: 'text/event-stream',
                    'Content-Type': 'application/json',
                    'X-API-VERSION': API_VERSION,
                    'X-TENANT-ID': tenantId,
                    'X-SID': browserSessionId,
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ chatId, message, requestId }),
                signal: abortControllerRef.current.signal,
                onmessage: (event) => {
                    try {
                        const data: StreamEvent = JSON.parse(event.data);
                        switch (data.type) {
                            case StreamEventType.STEP:
                                if (data.text) {
                                    callbacksRef.current.onStep(data.text);
                                }
                                break;
                            case StreamEventType.TOKEN:
                                if (data.data) {
                                    callbacksRef.current.onToken(data.data);
                                }
                                break;
                            case StreamEventType.COMPLETE:
                                if (data.data) {
                                    callbacksRef.current.onComplete(data.data);
                                }
                                break;
                            case StreamEventType.ERROR:
                                callbacksRef.current.onError(data.data || 'Unknown error');
                                break;
                        }
                    } catch (e) {
                        console.error('Failed to parse stream event:', e);
                    }
                },
                onerror: (err) => {
                    callbacksRef.current.onError(err.message || 'Stream connection failed');
                    throw err; // Stop reconnection attempts
                },
                openWhenHidden: true,
            });
        },
        []
    );

    const cancelStream = useCallback(() => {
        abortControllerRef.current?.abort();
    }, []);

    return { sendStreamingMessage, cancelStream };
}
